import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { ArchiveLot, ArchiveSale, formatRealized } from '../types/archive.types';

/**
 * PAST LOT — /past/:saleId/lots/:lotId. Public, no sign-in.
 * Reads ONLY archiveSales/{saleId} and archiveSales/{saleId}/lots/{lotId} (no customer data).
 *
 * HOOK: the "I Want It" / make-offer mechanism is being designed separately and will attach on this page,
 * in the slot marked below. Nothing is wired here yet, on purpose.
 */
const PastLotDetail: React.FC = () => {
  const { saleId, lotId } = useParams<{ saleId: string; lotId: string }>();
  const [sale, setSale] = useState<ArchiveSale | null | undefined>(undefined);
  const [lot, setLot] = useState<ArchiveLot | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [img, setImg] = useState(0);

  useEffect(() => {
    if (!saleId || !lotId) return;
    Promise.all([getDoc(doc(db, 'archiveSales', saleId)), getDoc(doc(db, 'archiveSales', saleId, 'lots', lotId))])
      .then(([s, l]) => {
        setSale(s.exists() ? { ...(s.data() as Omit<ArchiveSale, 'id'>), id: s.id } : null);
        setLot(l.exists() ? { ...(l.data() as Omit<ArchiveLot, 'id'>), id: l.id } : null);
      })
      .catch((err) => { console.error('Past lot load error:', err); setError(err.message); });
  }, [saleId, lotId]);

  if (error) return <div className="max-w-5xl mx-auto rounded-lg border border-red-300 bg-red-50 p-4 text-red-800">Could not load this lot: {error}</div>;
  if (sale === undefined || lot === undefined) return <p className="max-w-5xl mx-auto text-gray-600">Loading…</p>;
  if (!sale || !lot) return (
    <div className="max-w-5xl mx-auto space-y-3">
      <p className="font-semibold">This lot is not in the archive.</p>
      <Link to="/past" className="text-primary-600 hover:underline">← Past Auctions</Link>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto space-y-4">
      <Link to={`/past/${sale.id}`} className="text-sm text-primary-600 hover:underline">← {sale.title}</Link>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
            {lot.images[img] ? <img src={lot.images[img]} alt={lot.title} className="w-full h-full object-contain" />
              : <div className="w-full h-full flex items-center justify-center text-gray-400">No photo</div>}
          </div>
          {lot.images.length > 1 && (
            <div className="flex gap-2 flex-wrap">
              {lot.images.map((u, i) => (
                <button key={u} onClick={() => setImg(i)} className={`w-16 h-16 rounded border-2 overflow-hidden ${i === img ? 'border-primary-600' : 'border-transparent'}`}>
                  <img src={u} alt="" loading="lazy" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="space-y-4">
          <div>
            <p className="text-sm text-gray-500">Lot {lot.lotNumber}</p>
            <h1 className="text-2xl font-bold text-gray-900">{lot.title}</h1>
            {lot.category && <span className="inline-block mt-2 text-xs bg-gray-100 px-2 py-1 rounded">{lot.category}</span>}
          </div>
          <div className="rounded-lg border border-gray-200 p-4">
            {lot.result === 'sold' && lot.realizedCents != null ? (
              <><p className="text-sm text-gray-600">Sold</p><p className="text-3xl font-bold">{formatRealized(lot.realizedCents)}</p>
                <p className="text-xs text-gray-500 mt-1">Hammer price, before buyer's premium.</p></>
            ) : lot.result === 'unsold' ? (
              <p className="text-gray-700 font-semibold">Did not sell</p>
            ) : <p className="text-gray-600">Results not available for this sale.</p>}
          </div>
          {/* HOOK — "I Want It" / make-offer attaches here. Scoped separately; intentionally empty. */}
          {lot.condition && <p className="text-sm"><span className="font-semibold text-gray-600">Condition</span> {lot.condition}</p>}
          {lot.description && <p className="text-gray-700 whitespace-pre-wrap">{lot.description}</p>}
        </div>
      </div>
    </div>
  );
};

export default PastLotDetail;
